import type { CSSProperties } from 'react';
import type { StyleRule } from '@worldathletics/types';

/**
 * Convert a StyleRule (or null) into a CSSProperties object that sets
 * CSS custom properties on a block element.
 *
 * When `rule` is null, force-unsets every var to 'initial' so CSS
 * inheritance from a styled ancestor doesn't leak into a no-rule block.
 * The `initial` value makes `var(--name, <default>)` use its fallback.
 *
 * Design contract: cascade is per-style (resolved by `resolveStyleRule`),
 * NOT per-DOM-ancestry. This helper enforces that contract at every
 * styled block element.
 */
export function styleRuleToCssVars(rule: StyleRule | null): CSSProperties {
  if (rule) {
    return {
      ['--font-family' as string]: rule.fontFamily,
      ['--font-size' as string]: `${String(rule.fontSize)}pt`,
      ['--font-weight' as string]: rule.isBold ? '700' : '400',
      ['--font-style' as string]: rule.isItalic ? 'italic' : 'normal',
      ['--color' as string]: rule.color,
      ['--text-transform' as string]:
        rule.casing === 'uppercase' ? 'uppercase' : 'none',
      ['--indent-pt' as string]: `${String(rule.indentPt)}pt`,
    };
  }
  return {
    ['--font-family' as string]: 'initial',
    ['--font-size' as string]: 'initial',
    ['--font-weight' as string]: 'initial',
    ['--font-style' as string]: 'initial',
    ['--color' as string]: 'initial',
    ['--text-transform' as string]: 'initial',
    ['--indent-pt' as string]: 'initial',
  };
}

/**
 * Leaf text blocks (paragraph / note / exception) use product-fixed 16px
 * regular typography. Only non-size profile fields are applied as CSS vars.
 */
export function styleRuleToLeafCssVars(rule: StyleRule | null): CSSProperties {
  if (rule) {
    return {
      ['--font-family' as string]: rule.fontFamily,
      ['--color' as string]: rule.color,
      ['--text-transform' as string]:
        rule.casing === 'uppercase' ? 'uppercase' : 'none',
      ['--indent-pt' as string]: `${String(rule.indentPt)}pt`,
    };
  }
  return {
    ['--font-family' as string]: 'initial',
    ['--color' as string]: 'initial',
    ['--text-transform' as string]: 'initial',
    ['--indent-pt' as string]: 'initial',
  };
}
